import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Worksheet {
  title: string;
  topic: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  pages: number;
}

const worksheets: Worksheet[] = [
  // Unit 1
  { title: 'Evaluating Limits', topic: 'Limits & Continuity', difficulty: 'Medium', pages: 3 },
  { title: 'Continuity & IVT', topic: 'Limits & Continuity', difficulty: 'Medium', pages: 3 },
  // Unit 2
  { title: 'Limit Definition of Derivative', topic: 'Differentiation Basics', difficulty: 'Medium', pages: 3 },
  { title: 'Basic Differentiation Rules', topic: 'Differentiation Basics', difficulty: 'Easy', pages: 3 },
  // Unit 3
  { title: 'Chain Rule & Implicit Differentiation', topic: 'Advanced Differentiation', difficulty: 'Hard', pages: 4 },
  { title: 'Inverse & Logarithmic Differentiation', topic: 'Advanced Differentiation', difficulty: 'Hard', pages: 4 },
  // Unit 4
  { title: "Particle Motion & Related Rates", topic: 'Applications of Derivatives', difficulty: 'Hard', pages: 4 },
  { title: "L'Hôpital's Rule & Linearization", topic: 'Applications of Derivatives', difficulty: 'Medium', pages: 3 },
  // Unit 5
  { title: 'MVT & Optimization', topic: 'Analytical Applications', difficulty: 'Hard', pages: 4 },
  { title: 'Curve Sketching & Concavity', topic: 'Analytical Applications', difficulty: 'Medium', pages: 3 },
  // Unit 6
  { title: 'Antiderivatives & FTC', topic: 'Integration', difficulty: 'Medium', pages: 3 },
  { title: 'U-Substitution & Techniques', topic: 'Integration', difficulty: 'Hard', pages: 4 },
  // Unit 7
  { title: 'Slope Fields & Separable DEs', topic: 'Differential Equations', difficulty: 'Hard', pages: 4 },
  { title: 'Euler\'s Method & Growth Models', topic: 'Differential Equations', difficulty: 'Medium', pages: 3 },
  // Unit 8
  { title: 'Area Between Curves', topic: 'Applications of Integration', difficulty: 'Medium', pages: 3 },
  { title: 'Volumes of Revolution', topic: 'Applications of Integration', difficulty: 'Hard', pages: 4 },
];

const scheduleOptions = [
  { weeks: 4, label: '4-Week Sprint', description: 'Fast AP exam prep' },
  { weeks: 6, label: '6-Week Standard', description: 'Recommended pace' },
  { weeks: 8, label: '8-Week Deep Dive', description: 'Full mastery' },
  { weeks: 12, label: '12-Week Semester', description: 'Course-long review' },
];

const studyTips = [
  {
    icon: 'ri-functions',
    title: 'Master the Rule of Four',
    description: 'For every concept, understand it algebraically, graphically, numerically (tables), and verbally. The AP exam tests all four representations.',
  },
  {
    icon: 'ri-pencil-ruler-2-line',
    title: 'Practice FRQs Daily',
    description: 'Free Response Questions require written explanations. Practice writing full solutions with proper notation — units and justification are graded.',
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Connect f, f\', and f\'\'',
    description: 'The relationships between a function and its derivatives are central to the AB exam. Sketch all three from any given graph.',
  },
  {
    icon: 'ri-calculator-line',
    title: 'Use Your Calculator Wisely',
    description: 'The AP exam has calculator and non-calculator sections. Know when you can use it and which functions to master (intersect, integral, derivative, root).',
  },
];

export default function APCalcABStudyPlanPage() {
  const [selectedWeeks, setSelectedWeeks] = useState(6);

  const worksheetsPerWeek = Math.ceil(worksheets.length / selectedWeeks);
  const totalPages = worksheets.reduce((sum, w) => sum + w.pages, 0);
  const estimatedHours = Math.round((totalPages * 0.6 + worksheets.length * 0.5) * 10) / 10;

  const getWeeklySchedule = () => {
    const schedule: Worksheet[][] = [];
    for (let i = 0; i < selectedWeeks; i++) {
      const start = i * worksheetsPerWeek;
      const end = Math.min(start + worksheetsPerWeek, worksheets.length);
      const weekItems = worksheets.slice(start, end);
      if (weekItems.length > 0) schedule.push(weekItems);
    }
    return schedule;
  };

  const weeklySchedule = getWeeklySchedule();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <header className="bg-white border-b border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="inline-flex items-center text-amber-600 hover:text-amber-700 mb-3 text-sm font-medium whitespace-nowrap">
                <i className="ri-arrow-left-line mr-2"></i>Back to Home
              </Link>
              <h1 className="text-4xl font-bold text-gray-900">AP Calculus AB Study Plan</h1>
              <p className="text-lg text-gray-600 mt-2">8 Units · Limits → Integration — Structured Weekly Schedule</p>
            </div>
            <button onClick={() => window.print()} className="hidden sm:flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium whitespace-nowrap print:hidden">
              <i className="ri-printer-line"></i>Print Plan
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl border border-amber-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Study Timeline</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {scheduleOptions.map((option) => (
              <button key={option.weeks} onClick={() => setSelectedWeeks(option.weeks)}
                className={`p-6 rounded-xl border-2 transition-all text-left cursor-pointer ${selectedWeeks === option.weeks ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}>
                <div className="text-3xl font-bold text-amber-600 mb-2">{option.weeks}</div>
                <div className="font-semibold text-gray-900 mb-1">{option.label}</div>
                <div className="text-sm text-gray-600">{option.description}</div>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-8 border-t border-amber-100">
            <div className="text-center"><div className="text-3xl font-bold text-amber-600">{worksheets.length}</div><div className="text-sm text-gray-600 mt-1">Total Worksheets</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-amber-600">{totalPages}</div><div className="text-sm text-gray-600 mt-1">Total Pages</div></div>
            <div className="text-center"><div className="text-3xl font-bold text-amber-600">{estimatedHours}h</div><div className="text-sm text-gray-600 mt-1">Estimated Time</div></div>
          </div>
        </div>

        <div className="space-y-6 mb-8">
          {weeklySchedule.map((week, weekIndex) => {
            const weekPages = week.reduce((sum, w) => sum + w.pages, 0);
            return (
              <div key={weekIndex} className="bg-white rounded-xl border border-amber-100 overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">Week {weekIndex + 1}</h3>
                    <div className="flex items-center gap-6 text-white text-sm">
                      <span><i className="ri-file-list-line mr-1"></i>{week.length} worksheets</span>
                      <span><i className="ri-pages-line mr-1"></i>{weekPages} pages</span>
                    </div>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {week.map((worksheet, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-amber-50/50 rounded-lg border border-amber-100">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">{idx + 1}</div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{worksheet.title}</div>
                          <div className="text-sm text-gray-600 mt-0.5">{worksheet.topic}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${worksheet.difficulty === 'Easy' ? 'bg-green-100 text-green-700' : worksheet.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{worksheet.difficulty}</span>
                        <span className="text-sm text-gray-600 whitespace-nowrap">{worksheet.pages}p</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-amber-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">AP Calculus AB Study Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {studyTips.map((tip, index) => (
              <div key={index} className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                  <i className={`${tip.icon} text-2xl`}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/ap-calc-ab" className="bg-white rounded-xl border border-amber-100 p-6 hover:border-amber-300 transition-all group">
            <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center mb-4 group-hover:bg-amber-600 group-hover:text-white transition-colors">
              <i className="ri-book-open-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">AB Curriculum</h3>
            <p className="text-sm text-gray-600">Full lessons with 480+ practice problems</p>
          </Link>
          <Link to="/ap-calc-ab-progress" className="bg-white rounded-xl border border-amber-100 p-6 hover:border-amber-300 transition-all group">
            <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center mb-4 group-hover:bg-amber-600 group-hover:text-white transition-colors">
              <i className="ri-bar-chart-box-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Progress Tracker</h3>
            <p className="text-sm text-gray-600">Track completed topics and skills</p>
          </Link>
          <Link to="/ap-calc-bc" className="bg-white rounded-xl border border-amber-100 p-6 hover:border-amber-300 transition-all group">
            <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center mb-4 group-hover:bg-amber-600 group-hover:text-white transition-colors">
              <i className="ri-arrow-right-circle-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">AP Calculus BC</h3>
            <p className="text-sm text-gray-600">Continue to full BC curriculum</p>
          </Link>
        </div>
      </main>
    </div>
  );
}
